import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Spin,
  Button,
  Divider,
  Select,
  Table,
  Switch,
  InputNumber,
  Popconfirm,
  Tag,
  Input,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelopeCircleCheck, faFingerprint } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import WizardReadOnlyId from "../WizardReadOnlyId";
import WizardIdHoverTag from "../WizardIdHoverTag";
import GlobalAlert2 from "../../home/GlobalAlert2";
import SelectLn2Message from "../../components/select/SelectLn2Message";
// REDUX
import {
  usePostNotificationMutation,
  usePutNotificationMutation,
  useDeleteNotificationMutation,
  usePostAssignNotificationToUserMutation,
  useDeleteAssignNotificationToUserMutation,
  useLazyGetUsersByOrganizationQuery,
  useLazyGetNotificationsByOrganizationQuery,
  useLazyGetNotificationAssignmentsByOrganizationQuery,
} from "../../../redux/notifications/notificationAPI";
import { mapAssignmentsResponseToRecord } from "../../../redux/notifications/mapAssignmentsResponseToRecord";
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
import { useLazyGetAdapterQuery } from "../../../redux/adapters/adapterAPI";
import { useLazyGetRolesByOrganizationQuery } from "../../../redux/roles/roleAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate } from "../WizardStepGate";
// WIZARD UTILS
import { WizardStep } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
// STORE
import { RootState } from "../../../app/store";
// WIZARD COMPONENTS
import WizardExportButton from "../components/WizardExportButton";
// UTILS — protocol resolution mirrors StepActions (wizard map + adapter object)
import { resolveProtocolKey } from "./StepActions";
// FLATBUFFERS
import {
  LN2_MESSAGE_CATEGORIES,
  parseCompositeKey,
  getFullDisplayName,
  decodeFlattenedMessage,
  Ln2MessageFilter,
  ln2MessageFilterToKeySet,
} from "../../../flatbuffers/types/ln2MessageOptions";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { NotificationType } from "../../../flatbuffers/generated/archive/notification-type";
import { RecipientKind } from "../../../flatbuffers/generated/archive/recipient-kind";
import { MessageMatchMode } from "../../../flatbuffers/generated/archive/message-match-mode";
import { TriggerEdge } from "../../../flatbuffers/generated/archive/trigger-edge";
import formatUuidT from "../../../flatbuffers/display/uuid";
import { notificationTypeLabel } from "../../../flatbuffers/display/enums";

// One entry in a rule's audience: a literal address, a named person, or a role
// standing for whoever holds it.
//
// `kind` decides which of `value` / `userId` / `roleId` carries the target.
// ADDRESS is the default because every recipient created before groups existed
// is a literal one, and the backend's zero value agrees.
interface RecipientDraft {
  type: string;
  value: string;
  kind: RecipientKindName;
  userId: string;
  roleId: string;
  // Channel NAMES this entry should be reached on. Converted to the backend's
  // bit mask only when the request is built; an empty list means "unset — fall
  // back to `type`", which is how the backend reads a zero mask.
  channels: string[];
}

type RecipientKindName = "ADDRESS" | "USER" | "ROLE";

// Channel names, and the bit each maps to on the wire. The backend stores a
// mask so one entry can say "email AND text", which its single `type` column
// cannot express.
//
// The form holds the channels as NAMES, not as a mask. The repo forbids
// bitwise operators, and more usefully a list of names is what the UI actually
// renders and compares. The mask exists only at the boundary, where the bits —
// being disjoint powers of two — make a sum and a bitwise OR identical.
const CHANNEL_BITS: Record<string, number> = { EMAIL: 1, SMS: 2, WHATSAPP: 4 };

// Sum the selected channels into the mask the backend stores.
function channelsToMask(channels: string[]): number {
  return channels.reduce((mask, name) => mask + (CHANNEL_BITS[name] ?? 0), 0);
}

// Recover the channel names from a stored mask, without bit operators: a bit
// is set when dividing past it leaves an odd number.
function maskToChannels(mask: number): string[] {
  return Object.keys(CHANNEL_BITS).filter(
    (name) => Math.floor(mask / CHANNEL_BITS[name]) % 2 === 1,
  );
}

// A fresh literal-address entry.
function newAddressRecipient(): RecipientDraft {
  return { type: "EMAIL", value: "", kind: "ADDRESS", userId: "", roleId: "", channels: ["EMAIL"] };
}

interface NotificationRow {
  id: string;
  name: string;
  type: string;
  cooldown: number;
  min_span: number;
  muted: boolean;
  bank_id: string;
  message_id: string;
  messageName: string;
  match_mode: string;
  trigger_edge: string;
  recipients: RecipientDraft[];
}

// WhatsApp is deliberately absent. The backend resolves WhatsApp destinations
// and then drops them, because no WhatsApp transport is wired up — an operator
// who picked it got no message and no error. Offering a channel that silently
// discards is worse than not offering it; restore this option in the same
// change that adds the transport.
const NOTIFICATION_TYPE_OPTIONS = [
  { label: "Email", value: "EMAIL" },
  { label: "SMS", value: "SMS" },
];

// How a rule's stored message selector is matched. Mirrors
// Archive.MessageMatchMode; CATEGORY is the zero value and is what every rule
// created before this control existed already did.
const MATCH_MODE_OPTIONS = [
  { label: "Any message", value: "ANY" },
  { label: "Whole category", value: "CATEGORY" },
  { label: "Specific message", value: "EXACT" },
];

// Which transition of the message's active flag fires the rule.
const TRIGGER_EDGE_OPTIONS = [
  { label: "Activated", value: "ACTIVATED" },
  { label: "Cleared", value: "CLEARED" },
  { label: "Both", value: "BOTH" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,}$/;

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

function isValidPhone(value: string): boolean {
  return PHONE_REGEX.test(value.trim());
}

// A recipient is valid when its value matches the channel it was chosen for.
function isRecipientValid(recipient: RecipientDraft): boolean {
  if (recipient.value.trim() === "") return false;
  if (recipient.type === "EMAIL") return isValidEmail(recipient.value);
  return isValidPhone(recipient.value);
}

function areRecipientsValid(recipients: RecipientDraft[]): boolean {
  return recipients.length > 0 && recipients.every(isRecipientValid);
}

// Channel-specific placeholder so the value input reads as the right kind of
// field the moment the operator picks a channel.
function recipientPlaceholder(type: string): string {
  if (type === "EMAIL") return "name@example.com";
  if (type === "WHATSAPP") return "+1 555 123 4567 (WhatsApp number)";
  return "+1 555 123 4567 (phone number)";
}

// Derive the notification's stored channel from its recipients (first row).
// The backend keeps a single `type` column; recipients carry their own channel.
function deriveNotificationType(recipients: RecipientDraft[]): string {
  return recipients.length > 0 ? recipients[0].type : "EMAIL";
}

// Coerce a restored draft / API payload of unknown shape into RecipientDraft[].
// Always yields at least one row so the editor is never empty.
function sanitizeRecipientDrafts(raw: unknown[]): RecipientDraft[] {
  const cleaned = raw
    .filter((r): r is Record<string, unknown> => typeof r === "object" && r !== null)
    .map((r) => ({
      type: typeof r.type === "string" ? r.type : "EMAIL",
      value: typeof r.value === "string" ? r.value : "",
      kind: normalizeRecipientKind(r.kind),
      userId: typeof r.userId === "string" ? r.userId : "",
      roleId: typeof r.roleId === "string" ? r.roleId : "",
      channels: Array.isArray(r.channels) ? (r.channels as string[]) : ["EMAIL"],
    }));
  return cleaned.length > 0 ? cleaned : [newAddressRecipient()];
}

// Coerce a recipient `kind` from a server payload into the names the form
// uses. FlatBuffer responses decode it to the numeric Archive.RecipientKind;
// JSON gives the name. Anything unrecognised reads as ADDRESS, which is both
// the enum's zero value and what every pre-groups recipient is.
// What the rule listens to, in words.
function triggerSubject(matchMode: string, messageName: string): string {
  if (matchMode === "ANY") return "any message";
  if (matchMode === "EXACT") return messageName || "the selected message";

  const category = (messageName || "").split("::")[0];

  return `any ${category || "matching"} message`;
}

// Which transition the rule reacts to, in words.
function triggerEdgeVerb(triggerEdge: string): string {
  if (triggerEdge === "BOTH") return "activates or clears";
  if (triggerEdge === "CLEARED") return "clears";

  return "activates";
}

// Restate the trigger in words. Two enum dropdowns are easy to set and hard to
// read back; this is the sentence an operator can check at a glance.
export function describeTrigger(
  matchMode: string,
  triggerEdge: string,
  messageName: string,
): string {
  return `Alerts when ${triggerSubject(matchMode, messageName)} ${triggerEdgeVerb(triggerEdge)}.`;
}

/**
 * Protocols whose adapters do not publish `/keys` rows. The notification LN2
 * message combo is a FlatBuffer catalog filtered by those keys, so an empty
 * key set would hide every option. Leave the filter unset for these protocols
 * and the picker shows the full catalog (still message composite keys, never
 * an adapter–message tuple).
 *
 * MCE maps messages internally in VTS. Add other keyless protocols here.
 */
export const UNFILTERED_NOTIFICATION_MESSAGE_PROTOCOLS = new Set(["MCE"]);

/** True when the notification message combo should list every LN2 message. */
export function shouldShowAllNotificationMessages(
  protocolFromMap: string | undefined,
  adapter?: unknown,
): boolean {
  return UNFILTERED_NOTIFICATION_MESSAGE_PROTOCOLS.has(
    resolveProtocolKey(protocolFromMap, adapter),
  );
}

/**
 * The filter actually applied to the LN2 message combo.
 *
 * The operator switch is the future-proof escape hatch: any protocol that
 * under-reports or publishes no `/keys` can show the full catalog without a
 * code change. Protocol auto-unfilter (MCE) already leaves `adapterFilter`
 * undefined; the switch does the same for everything else.
 */
export function effectiveNotificationMessageFilter(
  adapterFilter: Ln2MessageFilter | undefined,
  ignoreFilter: boolean,
): Ln2MessageFilter | undefined {
  if (ignoreFilter) return undefined;
  return adapterFilter;
}

function normalizeRecipientKind(raw: unknown): RecipientKindName {
  const name = typeof raw === "number" ? RecipientKind[raw] : raw;
  if (name === "USER" || name === "ROLE") return name;
  return "ADDRESS";
}

// Coerce a `match_mode` from a server payload into the option names the
// "Fires on" select uses. The wire may carry the numeric MessageMatchMode
// (FlatBuffer, or JSON from the REST API) or the name. Anything unrecognised
// reads as CATEGORY, which is the enum's zero value and what every rule created
// before the field existed already did.
function normalizeMatchMode(raw: unknown): string {
  const name = typeof raw === "number" ? MessageMatchMode[raw] : raw;
  if (name === "ANY" || name === "EXACT") return name;
  return "CATEGORY";
}

// Same coercion for `trigger_edge` ("When the message is"). Unrecognised
// reads as ACTIVATED, the enum's zero value.
function normalizeTriggerEdge(raw: unknown): string {
  const name = typeof raw === "number" ? TriggerEdge[raw] : raw;
  if (name === "CLEARED" || name === "BOTH") return name;
  return "ACTIVATED";
}

// Normalize a notification/recipient channel from a server payload into the
// "EMAIL" | "SMS" | "WHATSAPP" strings the form uses. FlatBuffer responses
// decode `type` to a numeric NotificationType enum; JSON responses give a
// string. Reverse-lookup the enum name (SMS=0, EMAIL=1, WHATSAPP=2).
function normalizeNotifType(value: unknown): string {
  if (typeof value === "number") return NotificationType[value] ?? "EMAIL";
  if (typeof value === "string" && value.trim() !== "") return value.toUpperCase();
  return "EMAIL";
}

// Reconstruct the composite message key + display name from the flattened
// (messageContent, messageValue) pair the backend persists. Inverse of the
// category -> messageContent and option -> enumValue mapping used on create.
// Exported for unit testing.
export function messageKeyFromFlattened(
  messageContent: number,
  messageValue: number,
): { key: string; name: string } | null {
  const decoded = decodeFlattenedMessage(messageContent, messageValue);
  return decoded ? { key: decoded.key, name: decoded.fullName } : null;
}

// Read a numeric field from a server notification that may be FlatBuffer
// (camelCase) or JSON (snake_case).
function pickNumField(
  n: Record<string, unknown>,
  camel: string,
  snake: string,
  fallback = 0,
): number {
  const raw = n[camel] ?? n[snake];
  if (typeof raw === "number") return raw;
  const coerced = Number(raw);
  return Number.isFinite(coerced) ? coerced : fallback;
}

// Map a server notification (FlatBuffer camelCase OR JSON snake_case) into the
// NotificationRow shape the wizard table renders. Returns null when the record
// lacks the ids needed to render/edit it. Exported for unit testing.
export function mapServerNotificationToRow(n: Record<string, unknown>): NotificationRow | null {
  const id =
    formatUuidT(n.id as Parameters<typeof formatUuidT>[0]) ||
    (typeof n.id === "string" ? n.id : "");
  const rawBank = n.bankId ?? n.bank_id;
  const bankId =
    formatUuidT(rawBank as Parameters<typeof formatUuidT>[0]) ||
    (typeof rawBank === "string" ? rawBank : "");
  if (!id || !bankId) return null;

  const messageContent = pickNumField(n, "messageContent", "message_content");
  const messageValue = pickNumField(n, "messageValue", "message_value");
  const message = messageKeyFromFlattened(messageContent, messageValue);

  const rawRecipients = Array.isArray(n.recipients) ? n.recipients : [];
  const recipients: RecipientDraft[] = rawRecipients
    .filter((r): r is Record<string, unknown> => typeof r === "object" && r !== null)
    .map((r) => ({
      type: normalizeNotifType(r.type),
      value: typeof r.value === "string" ? r.value : String(r.value ?? ""),
      kind: normalizeRecipientKind(r.kind),
      userId: formatUuidT(r.userId as Parameters<typeof formatUuidT>[0]) || "",
      roleId: formatUuidT(r.roleId as Parameters<typeof formatUuidT>[0]) || "",
      // A payload with no mask is a pre-mask recipient: the backend reads a
      // zero mask as "fall back to type", so mirror that rather than assuming
      // email — otherwise an SMS-only recipient silently reads as email.
      channels: maskToChannels(pickNumField(r, "channels", "channels", 0)).length
        ? maskToChannels(pickNumField(r, "channels", "channels", 0))
        : [normalizeNotifType(r.type)],
    }));

  return {
    id,
    name: typeof n.name === "string" ? n.name : String(n.name ?? ""),
    type: normalizeNotifType(n.type),
    cooldown: pickNumField(n, "cooldown", "cooldown", 300),
    min_span: pickNumField(n, "minSpan", "min_span", 15),
    muted: Boolean(n.muted),
    bank_id: bankId,
    message_id: message?.key ?? "",
    messageName: message?.name ?? "",
    match_mode: normalizeMatchMode(n.matchMode ?? n.match_mode),
    trigger_edge: normalizeTriggerEdge(n.triggerEdge ?? n.trigger_edge),
    recipients,
  };
}

export default function StepNotifications() {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, setState: setWizardState, markStepCompleted, goToStep } = useWizard();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [selectedBankId, setSelectedBankId] = useState<string | undefined>(undefined);
  const [notifName, setNotifName] = useState("");
  const [notifRecipients, setNotifRecipients] = useState<RecipientDraft[]>([newAddressRecipient()]);
  const [notifCooldown, setNotifCooldown] = useState<number>(300);
  const [notifMinSpan, setNotifMinSpan] = useState<number>(15);
  const [notifMuted, setNotifMuted] = useState(false);
  const [notifMessageId, setNotifMessageId] = useState("");
  // CATEGORY is the default because it is what the delivery engine already
  // did: it never read message_value, so every rule made before this control
  // existed fires on the whole category regardless of the message picked.
  const [matchMode, setMatchMode] = useState("CATEGORY");
  const [triggerEdge, setTriggerEdge] = useState("ACTIVATED");
  const [notifMessageName, setNotifMessageName] = useState("");
  const [notificationsCreated, setNotificationsCreated] = useState<NotificationRow[]>([]);
  const [adapterMessageFilter, setAdapterMessageFilter] = useState<Ln2MessageFilter | undefined>(
    undefined,
  );
  // Operator escape hatch: ignore the adapter-key filter and offer the full
  // LN2 catalog. Off by default and reset on bank change, matching Actions'
  // "Show all control commands". Protocols that auto-unfilter (MCE) already
  // have no filter; this switch is what keeps any other keyless protocol
  // from hard-blocking the step.
  const [ignoreMessageFilter, setIgnoreMessageFilter] = useState(false);

  // Edit mode
  const [editingNotifId, setEditingNotifId] = useState<string | null>(null);

  const [assignments, setAssignments] = useState<Record<string, string[]>>({});
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
  const [roleNameMap, setRoleNameMap] = useState<Record<string, string>>({});
  const [roleUsersMap, setRoleUsersMap] = useState<Record<string, string[]>>({});

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // Notifications are saved server-side via the Save button (which
  // calls POST /notifications/). Until that click, the operator's
  // half-typed name, cooldowns, types, message selection, etc. live in
  // React state. Persist those so a reload doesn't blank the form.
  //
  // Verification: selectedBankId, editingNotifId are restored only if
  // they still refer to live entities in this wizard (a stale bank id
  // from a deleted/reorganized wizard would just stay undefined). Other
  // fields are pure operator input and round-trip as primitives.
  useWizardStepDraft<{
    selectedBankId: string | undefined;
    notifName: string;
    notifRecipients: RecipientDraft[];
    notifCooldown: number;
    notifMinSpan: number;
    notifMuted: boolean;
    notifMessageId: string;
    notifMessageName: string;
    editingNotifId: string | null;
  }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Notifications,
    scopeId: "notifications-form",
    scopeType: "step",
    draft: {
      selectedBankId,
      notifName,
      notifRecipients,
      notifCooldown,
      notifMinSpan,
      notifMuted,
      notifMessageId,
      notifMessageName,
      editingNotifId,
    },
    onRestore: (saved) => {
      if (
        typeof saved.selectedBankId === "string" &&
        wizardState.bankIds.includes(saved.selectedBankId)
      ) {
        setSelectedBankId(saved.selectedBankId);
      }
      if (typeof saved.notifName === "string") setNotifName(saved.notifName);
      if (Array.isArray(saved.notifRecipients)) {
        setNotifRecipients(sanitizeRecipientDrafts(saved.notifRecipients));
      }
      if (typeof saved.notifCooldown === "number") setNotifCooldown(saved.notifCooldown);
      if (typeof saved.notifMinSpan === "number") setNotifMinSpan(saved.notifMinSpan);
      if (typeof saved.notifMuted === "boolean") setNotifMuted(saved.notifMuted);
      if (typeof saved.notifMessageId === "string") setNotifMessageId(saved.notifMessageId);
      if (typeof saved.notifMessageName === "string") setNotifMessageName(saved.notifMessageName);
      if (typeof saved.editingNotifId === "string") setEditingNotifId(saved.editingNotifId);
    },
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostNotification,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostNotificationMutation();
  const [
    triggerPutNotification,
    { isSuccess: isSuccessPut, isLoading: isLoadingPut, isError: isErrorPut, error: errorPut },
  ] = usePutNotificationMutation();
  const [triggerDeleteNotification, { isLoading: isLoadingDelete }] =
    useDeleteNotificationMutation();
  const [triggerGetAdapterKeys] = useLazyGetAdapterKeysQuery();
  const [triggerGetAdapter] = useLazyGetAdapterQuery();
  const [triggerPostAssign] = usePostAssignNotificationToUserMutation();
  const [triggerDeleteAssign] = useDeleteAssignNotificationToUserMutation();
  const [triggerGetUsers] = useLazyGetUsersByOrganizationQuery();
  const [triggerGetRoles] = useLazyGetRolesByOrganizationQuery();
  const [triggerGetNotifications] = useLazyGetNotificationsByOrganizationQuery();
  const [triggerGetAssignments] = useLazyGetNotificationAssignmentsByOrganizationQuery();

  // Notifications created earlier (or in a previous visit / before a refresh)
  // live server-side but the visible table is driven by local
  // `notificationsCreated` state, which resets on remount. Rehydrate it once
  // per org from the persisted notifications so a reload doesn't blank the
  // table (LIFT-276). Merge (dedupe by id) so optimistic local rows survive.
  const hydratedOrgRef = useRef<string | null>(null);
  useEffect(() => {
    const orgId = wizardState.organizationId;
    let cancelled = false;
    if (orgId && hydratedOrgRef.current !== orgId) {
      hydratedOrgRef.current = orgId;
      (async () => {
        try {
          const token = await GETJwtToken();
          const raw = await triggerGetNotifications({ orgId, token }).unwrap();
          const list: unknown[] = Array.isArray(raw)
            ? raw
            : ((raw as { data?: unknown[] } | null)?.data ?? []);
          const rows = list
            .filter((n): n is Record<string, unknown> => typeof n === "object" && n !== null)
            .map(mapServerNotificationToRow)
            .filter((r): r is NotificationRow => r !== null);
          if (cancelled || rows.length === 0) return;
          setNotificationsCreated((prev) => {
            const seen = new Set(prev.map((n) => n.id));
            return [...prev, ...rows.filter((r) => !seen.has(r.id))];
          });
        } catch {
          // Non-fatal: the table simply keeps whatever was created this session.
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [wizardState.organizationId, triggerGetNotifications]);

  // Hydrate assignments from server once per org (LIFT-XXX).
  // The assignments state tracks "Assign to Users" selections made in the current
  // session, but those choices are also persisted in notification_users rows.
  // Without this hydration, revisiting the step shows empty selectors even though
  // assignments already exist in the DB.
  useEffect(() => {
    const orgId = wizardState.organizationId;
    let cancelled = false;
    if (orgId && hydratedOrgRef.current === orgId) {
      // Only hydrate assignments after notifications have been hydrated.
      // Use the same hydratedOrgRef guard to ensure we've already fetched
      // the notification list.
      (async () => {
        try {
          const token = await GETJwtToken();
          const raw = await triggerGetAssignments({ orgId, token }).unwrap();
          const list: unknown[] = Array.isArray(raw)
            ? raw
            : ((raw as { data?: unknown[] } | null)?.data ?? []);
          if (cancelled) return;
          const serverAssignments = mapAssignmentsResponseToRecord(
            list.filter((a): a is Record<string, unknown> => typeof a === "object" && a !== null),
          );
          // Merge: only fill in keys not already present (don't clobber session work)
          setAssignments((prev) => ({
            ...serverAssignments,
            ...prev,
          }));
        } catch {
          // Non-fatal: assignments simply keep what was set in this session.
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [wizardState.organizationId, triggerGetAssignments]);

  // Auto-mark this step complete whenever the operator is on it.
  // Notifications are optional — many orgs add them later from the
  // dashboard, and Next must work without the operator clicking
  // around. Saving a real notification still flows through
  // handleSave; this just guarantees the step's completion gate
  // isn't a wall when the operator has nothing to add. Depending on
  // `currentStep` re-fires this if an upstream step unmarked us.
  useEffect(() => {
    if (wizardState.currentStep === WizardStep.Notifications) {
      markStepCompleted(WizardStep.Notifications);
    }
  }, [wizardState.currentStep, markStepCompleted]);

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const onChangeFormValue = (newValue: string) => {
    setNotifName(newValue);
  };

  const isFormValid = () =>
    notifName.trim() !== "" &&
    !!selectedBankId &&
    areRecipientsValid(notifRecipients) &&
    notifMessageId.trim() !== "";

  // ---- Recipients editor row mutations ----
  const handleAddRecipientRow = () => {
    setNotifRecipients((prev) => [...prev, newAddressRecipient()]);
  };

  const handleRemoveRecipientRow = (index: number) => {
    setNotifRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  /** Replace one audience entry's channel selection. */
  const handleRecipientChannels = (index: number, channels: string[]) => {
    setNotifRecipients((prev) => prev.map((r, i) => (i === index ? { ...r, channels } : r)));
  };

  const handleRecipientChange = (index: number, field: "type" | "value", value: string) => {
    setNotifRecipients((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  /** Build the backend-compatible body for a given message key and notification parameters. */
  const buildBodyForMessage = (
    messageId: string,
    name: string,
    recipients: RecipientDraft[],
    cooldown: number,
    minSpan: number,
    muted: boolean,
  ) => {
    const parsed = parseCompositeKey(messageId);
    if (!parsed) return null;
    const catIndex = LN2_MESSAGE_CATEGORIES.findIndex((c) => c.categoryKey === parsed.categoryKey);
    if (catIndex < 0) return null;
    const category = LN2_MESSAGE_CATEGORIES[catIndex];
    const messageContent = category.messageContent;
    const encodedType: number = category.encodedMessageContent;
    const fbsDeviceType =
      (selectedBankId && wizardState.bankTypes[selectedBankId]) || LiftType[LiftType.ELEVATOR];
    const validRecipients = recipients.filter(isRecipientValid);

    return {
      name,
      // The backend keeps a single `type` column per notification; the per-row
      // channel lives on each recipient. The FlatBuffer request serializer reads
      // singular `type` (str(body, "type")) — without it every notification's
      // stored type silently defaults to EMAIL. `types` is retained for the
      // legacy assignment send-path that still routes by a channel array.
      type: deriveNotificationType(validRecipients),
      types: [deriveNotificationType(validRecipients)],
      recipients: validRecipients.map((r) => ({
        type: r.type,
        // A USER or ROLE entry resolves its destinations at delivery time, so
        // it carries no literal value. Sending one would freeze a snapshot of
        // exactly the kind live groups exist to replace.
        value: r.kind === "ADDRESS" ? r.value : "",
        kind: r.kind,
        user_id: r.kind === "USER" ? r.userId : "",
        role_id: r.kind === "ROLE" ? r.roleId : "",
        channels: channelsToMask(r.channels),
      })),
      cooldown,
      min_span: minSpan,
      muted,
      // bank_ids is the rule's real scope; bank_id is sent alongside it purely
      // for readers that predate bank sets, and the backend stops matching on
      // it. Both are sent so an older cloud still understands the request.
      bank_ids: selectedBankId ? [selectedBankId] : [],
      bank_id: selectedBankId,
      organization_id: wizardState.organizationId,
      fbs_device_type: fbsDeviceType,
      encoded_type: encodedType,
      message_content: messageContent,
      // In CATEGORY or ANY mode the value is not consulted, but it is still
      // sent so switching a rule back to EXACT does not lose which message the
      // operator originally picked.
      message_value: parsed.enumValue,
      match_mode: matchMode,
      trigger_edge: triggerEdge,
      // Union of the audience's channel masks. The backend prefers each
      // recipient's own mask; this is the fallback for any that carry none.
      channels: channelsToMask(
        validRecipients.flatMap((r) => r.channels).filter((c, i, all) => all.indexOf(c) === i),
      ),
    };
  };

  /** Build the backend-compatible body from current form state. */
  const buildNotificationBody = (recipients: RecipientDraft[]) =>
    buildBodyForMessage(
      notifMessageId,
      notifName,
      recipients,
      notifCooldown,
      notifMinSpan,
      notifMuted,
    );

  const handleAddNotification = async () => {
    if (!isFormValid()) return;
    const token = await GETJwtToken();
    const body = buildNotificationBody(notifRecipients);
    if (!body) return;
    triggerPostNotification({ body, token });
  };

  const handleUpdateNotification = async () => {
    if (!isFormValid() || !editingNotifId) return;
    const token = await GETJwtToken();
    const body = buildNotificationBody(notifRecipients);
    if (!body) return;
    const savedRecipients = notifRecipients.filter(isRecipientValid);
    try {
      await triggerPutNotification({
        id: editingNotifId,
        body,
        token,
      }).unwrap();

      setNotificationsCreated((prev) =>
        prev.map((n) =>
          n.id === editingNotifId
            ? {
                ...n,
                name: notifName,
                type: deriveNotificationType(savedRecipients),
                cooldown: notifCooldown,
                min_span: notifMinSpan,
                muted: notifMuted,
                message_id: notifMessageId,
                messageName: notifMessageName,
                match_mode: matchMode,
                trigger_edge: triggerEdge,
                recipients: savedRecipients,
              }
            : n,
        ),
      );
      setEditingNotifId(null);
      clearForm();
    } catch {
      // Error shown by GlobalAlert2
    }
  };

  const handleDeleteNotification = async (notifId: string) => {
    const token = await GETJwtToken();
    try {
      await triggerDeleteNotification({ id: notifId, token }).unwrap();
      setNotificationsCreated((prev) => prev.filter((n) => n.id !== notifId));
      setWizardState((prev) => ({
        ...prev,
        notificationIds: prev.notificationIds.filter((id: string) => id !== notifId),
      }));
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[notifId];
        return next;
      });
      if (editingNotifId === notifId) {
        setEditingNotifId(null);
        clearForm();
      }
    } catch {
      // Error shown by GlobalAlert2
    }
  };

  const handleEditNotification = (row: NotificationRow) => {
    setEditingNotifId(row.id);
    setNotifName(row.name);
    setNotifRecipients(
      row.recipients.length > 0
        ? row.recipients.map((r) => ({ ...r }))
        : [{ ...newAddressRecipient(), type: row.type }],
    );
    setNotifCooldown(row.cooldown);
    setNotifMinSpan(row.min_span);
    setNotifMuted(row.muted);
    setNotifMessageId(row.message_id);
    setNotifMessageName(row.messageName);
    setMatchMode(row.match_mode);
    setTriggerEdge(row.trigger_edge);
  };

  const cancelEdit = () => {
    setEditingNotifId(null);
    clearForm();
  };

  const handleSkip = () => {
    markStepCompleted(WizardStep.Notifications);
    // Originally `goToStep(10)` — Notifications used to live at
    // index 9, so 10 meant "next step (Actions)." After
    // ChannelModifiers was inserted at index 5 the literal still
    // pointed at the *current* step, leaving operators stuck on
    // Notifications when they clicked Skip. Use the enum.
    goToStep(WizardStep.Actions);
  };

  const clearForm = () => {
    setNotifName("");
    setNotifRecipients([newAddressRecipient()]);
    setNotifCooldown(300);
    setNotifMinSpan(15);
    setNotifMuted(false);
    setNotifMessageId("");
    setNotifMessageName("");
    setMatchMode("CATEGORY");
    setTriggerEdge("ACTIVATED");
  };

  const filteredNotifications = notificationsCreated.filter((n) => n.bank_id === selectedBankId);

  const effectiveMessageFilter = effectiveNotificationMessageFilter(
    adapterMessageFilter,
    ignoreMessageFilter,
  );

  /** Returns all available message composite keys given the current adapterMessageFilter. */
  const getAvailableMessageKeys = (): { key: string; displayName: string }[] => {
    const keySet = effectiveMessageFilter ? ln2MessageFilterToKeySet(effectiveMessageFilter) : null;
    return LN2_MESSAGE_CATEGORIES.flatMap((cat) => {
      const seen = new Set<number>();
      return cat.options
        .filter((opt) => {
          if (seen.has(opt.enumValue)) return false;
          seen.add(opt.enumValue);
          if (!keySet) return true;
          return keySet.has(`${cat.categoryKey}:${opt.enumValue}`);
        })
        .map((opt) => ({
          key: `${cat.categoryKey}:normal:${opt.enumValue}`,
          displayName: `${cat.label}::${opt.label}`,
        }));
    });
  };

  /** Bulk-create one notification per available filtered message, skipping existing ones. */
  const handleAddAllMessages = async () => {
    if (!selectedBankId) return;
    const available = getAvailableMessageKeys();
    const token = await GETJwtToken();
    const existingMessageIds = new Set(
      notificationsCreated.filter((n) => n.bank_id === selectedBankId).map((n) => n.message_id),
    );
    const bankName = wizardState.bankNames[selectedBankId] || selectedBankId;

    await Promise.allSettled(
      available
        .filter(({ key }) => !existingMessageIds.has(key))
        .map(({ key, displayName }) => {
          const name = `${bankName} - ${displayName}`;
          const body = buildBodyForMessage(
            key,
            name,
            notifRecipients,
            notifCooldown,
            notifMinSpan,
            notifMuted,
          );
          if (!body) return Promise.resolve();
          return triggerPostNotification({ body, token })
            .unwrap()
            .catch(() => {});
        }),
    );
  };

  /** Handle multi-select user assignment change for a notification row. */
  const handleAssignmentChange = async (notifId: string, newUserIds: string[]) => {
    const token = await GETJwtToken();
    const currentIds = assignments[notifId] ?? [];
    const added = newUserIds.filter((id) => !currentIds.includes(id));
    const removed = currentIds.filter((id) => !newUserIds.includes(id));

    await Promise.allSettled([
      ...added.map((userId) =>
        triggerPostAssign({
          body: { notification_id: notifId, user_id: userId, muted: false },
          token,
        })
          .unwrap()
          .catch(() => {}),
      ),
      ...removed.map((userId) =>
        triggerDeleteAssign({ notificationId: notifId, userId, token })
          .unwrap()
          .catch(() => {}),
      ),
    ]);

    setAssignments((prev) => ({ ...prev, [notifId]: newUserIds }));
  };

  /** Assign all users belonging to a role to the given notification. */
  const handleAssignByRole = async (notifId: string, roleId: string) => {
    if (!roleId) return;
    const usersInRole = roleUsersMap[roleId] ?? [];
    if (usersInRole.length === 0) return;

    const token = await GETJwtToken();
    const currentIds = assignments[notifId] ?? [];
    const toAdd = usersInRole.filter((uid) => !currentIds.includes(uid));

    await Promise.allSettled(
      toAdd.map((userId) =>
        triggerPostAssign({
          body: { notification_id: notifId, user_id: userId, muted: false },
          token,
        })
          .unwrap()
          .catch(() => {}),
      ),
    );

    const merged = Array.from(new Set([...currentIds, ...usersInRole]));
    setAssignments((prev) => ({ ...prev, [notifId]: merged }));
  };

  /** Fetch adapter keys and build the message filter set. */
  const loadAdapterMessages = async (bankId: string) => {
    setAdapterMessageFilter(undefined);
    const adapterId = wizardState.adapterIdByBank[bankId];
    if (!adapterId) return;

    // Prefer the wizard's per-bank protocol map: GET /banks/:id often has no
    // nested adapter (Actions already documents this for MODBUS/OPCUA/LV2).
    const protocolFromMap = wizardState.adapterProtocolByBank?.[bankId];
    if (shouldShowAllNotificationMessages(protocolFromMap)) return;

    const token = await GETJwtToken();

    // First-time wizard sessions may not have adapterProtocolByBank yet (that
    // map is filled on hydrate). Fetch the adapter itself — not the bank —
    // because protocol lives on the adapter, and the combo still selects a
    // message composite key rather than an adapter–key tuple.
    const adapterResult = await triggerGetAdapter({ id: adapterId, token });
    const adapter = (adapterResult.data as any)?.data || adapterResult.data;
    if (shouldShowAllNotificationMessages(protocolFromMap, adapter)) return;

    const keysResult = await triggerGetAdapterKeys({ adapterId, token });
    const keysRaw: unknown = (keysResult.data as any)?.data ?? keysResult.data ?? [];
    const keys: any[] = Array.isArray(keysRaw) ? keysRaw : [];
    const filter: Ln2MessageFilter = new Map();
    keys.forEach((k: any) => {
      const mc: MessageContent = k.messageContent ?? k.message_content;
      if (
        mc < MessageContent.Status_StatusMessage ||
        mc > MessageContent.ControllerInfo_ControllerInfoMessage
      )
        return;
      const mv: number = k.messageValue ?? k.message_value;
      const existing = filter.get(mc);
      if (existing) {
        existing.add(mv);
      } else {
        filter.set(mc, new Set([mv]));
      }
    });
    setAdapterMessageFilter(filter);
  };

  /** Fetch user and role data for the assignment UI. */
  const loadUsersAndRoles = async () => {
    const orgId = wizardState.organizationId;
    if (!orgId) return;
    const token = await GETJwtToken();

    const [usersResult, rolesResult] = await Promise.allSettled([
      triggerGetUsers({
        searchAndFilterString: `?page=1&limit=9999&filter_target=organization_id&filter=${orgId}`,
        token,
      }).unwrap(),
      triggerGetRoles({ orgId, token }).unwrap(),
    ]);

    const usersData: any[] =
      usersResult.status === "fulfilled"
        ? ((usersResult.value as any)?.data ?? usersResult.value ?? [])
        : [];

    const rolesData: any[] =
      rolesResult.status === "fulfilled"
        ? ((rolesResult.value as any)?.data ?? rolesResult.value ?? [])
        : [];

    const newUserNameMap: Record<string, string> = {};
    const newRoleUsersMap: Record<string, string[]> = {};

    usersData.forEach((u: any) => {
      const uid = formatUuidT(u.id) ?? "";
      if (!uid) return;
      const first = u.first_name ?? u.firstName ?? "";
      const last = u.last_name ?? u.lastName ?? "";
      newUserNameMap[uid] = `${first} ${last}`.trim() || u.email || uid;
      const rid = formatUuidT(u.role_id ?? u.roleId) ?? "";
      if (rid) {
        if (!newRoleUsersMap[rid]) newRoleUsersMap[rid] = [];
        newRoleUsersMap[rid].push(uid);
      }
    });

    const newRoleNameMap: Record<string, string> = {};
    rolesData.forEach((r: any) => {
      const rid = formatUuidT(r.id) ?? "";
      if (rid) newRoleNameMap[rid] = r.name || r.displayName || rid;
    });

    setUserNameMap(newUserNameMap);
    setRoleNameMap(newRoleNameMap);
    setRoleUsersMap(newRoleUsersMap);
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */
  useEffect(() => {
    if (!isSuccessPost || !dataPost) return;
    // Backend returns an array (one notification per selected type)
    const notifArray: any[] = dataPost.data || dataPost;
    if (!Array.isArray(notifArray) || notifArray.length === 0) return;

    const newIds = notifArray.map((n: any) => formatUuidT(n.id) ?? "").filter(Boolean);
    setWizardState((prev) => ({
      ...prev,
      notificationIds: [...prev.notificationIds, ...newIds],
    }));
    setNotificationsCreated((prev) => [
      ...prev,
      ...notifArray.map((n: any) => ({
        id: formatUuidT(n.id) ?? "",
        name: (n.name as string | null) || notifName,
        type:
          typeof n.type === "number"
            ? notificationTypeLabel(n.type)
            : (n.type as string) || "EMAIL",
        cooldown: n.cooldown ?? notifCooldown,
        min_span: n.minSpan ?? n.min_span ?? notifMinSpan,
        muted: n.muted ?? notifMuted,
        bank_id: selectedBankId || "",
        message_id: notifMessageId,
        messageName: notifMessageName,
        match_mode: normalizeMatchMode(n.matchMode ?? n.match_mode ?? matchMode),
        trigger_edge: normalizeTriggerEdge(n.triggerEdge ?? n.trigger_edge ?? triggerEdge),
        // Recipients echo what was just submitted; used to prefill on edit.
        recipients: notifRecipients.filter(isRecipientValid),
      })),
    ]);
    clearForm();
  }, [isSuccessPost, dataPost]);

  // Mark step completed when at least 1 notification
  useEffect(() => {
    const hasNotification =
      notificationsCreated.length > 0 || wizardState.notificationIds.length > 0;
    if (hasNotification) {
      markStepCompleted(WizardStep.Notifications);
    }
  }, [notificationsCreated]);

  // Pre-select first bank if available
  useEffect(() => {
    if (wizardState.bankIds.length > 0 && !selectedBankId) {
      setSelectedBankId(wizardState.bankIds[0]);
    }
  }, [wizardState.bankIds]);

  // Load adapter messages when bank changes
  useEffect(() => {
    setIgnoreMessageFilter(false);
    if (selectedBankId) {
      loadAdapterMessages(selectedBankId);
    } else {
      setAdapterMessageFilter(undefined);
    }
  }, [selectedBankId]);

  useEffect(() => {
    loadUsersAndRoles();
  }, [wizardState.organizationId]);

  // ************************************************ */
  // DERIVED *************************************** */
  const availableMessages = useMemo(() => getAvailableMessageKeys(), [effectiveMessageFilter]);

  const userOptions = useMemo(
    () => wizardState.userIds.map((uid) => ({ label: userNameMap[uid] || uid, value: uid })),
    [wizardState.userIds, userNameMap],
  );

  const roleOptions = useMemo(
    () => wizardState.roleIds.map((rid) => ({ label: roleNameMap[rid] || rid, value: rid })),
    [wizardState.roleIds, roleNameMap],
  );

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  const notificationColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: NotificationRow) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {name}
          <WizardIdHoverTag value={record.id}>
            <FontAwesomeIcon
              icon={faFingerprint}
              style={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }}
            />
          </WizardIdHoverTag>
        </span>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "recipients",
      key: "recipients",
      render: (recipients: RecipientDraft[], record: NotificationRow) => {
        if (!recipients || recipients.length === 0) return record.type;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {recipients.map((r, i) => (
              <Tag key={i}>{`${r.type}: ${r.value}`}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Cooldown",
      dataIndex: "cooldown",
      key: "cooldown",
      render: (value: number) => `${value}s`,
    },
    {
      title: "Min Span",
      dataIndex: "min_span",
      key: "min_span",
      render: (value: number) => `${value}s`,
    },
    {
      title: "Muted",
      dataIndex: "muted",
      key: "muted",
      render: (value: boolean) => {
        if (value) return "Yes";
        return "No";
      },
    },
    {
      title: "Message",
      dataIndex: "messageName",
      key: "messageName",
    },
    {
      title: "Fires on",
      dataIndex: "match_mode",
      key: "match_mode",
      render: (value: string) => MATCH_MODE_OPTIONS.find((o) => o.value === value)?.label ?? value,
    },
    {
      title: "When the message is",
      dataIndex: "trigger_edge",
      key: "trigger_edge",
      render: (value: string) =>
        TRIGGER_EDGE_OPTIONS.find((o) => o.value === value)?.label ?? value,
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_: unknown, record: NotificationRow) => (
        <div style={{ display: "flex", gap: 4 }}>
          <WizardOpGate op="update">
            <Button type="link" size="small" onClick={() => handleEditNotification(record)}>
              Edit
            </Button>
          </WizardOpGate>
          <WizardOpGate op="delete">
            <Popconfirm
              title="Delete this notification?"
              onConfirm={() => handleDeleteNotification(record.id)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger size="small" loading={isLoadingDelete}>
                Delete
              </Button>
            </Popconfirm>
          </WizardOpGate>
        </div>
      ),
    },
  ];

  const expandedRowRender = (record: NotificationRow) => {
    const assignedIds = assignments[record.id] ?? [];

    return (
      <div style={{ padding: "8px 16px" }}>
        {/* Both selects persist assignments as soon as they change. */}
        <WizardOpGate op="update">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 300px", minWidth: 200 }}>
              <span className="generalStyles__inputLabel">Assign to Users</span>
              <Select
                mode="multiple"
                style={{ width: "100%", marginTop: 4 }}
                placeholder="Select users"
                value={assignedIds}
                onChange={(ids: string[]) => handleAssignmentChange(record.id, ids)}
                options={userOptions}
                tagRender={({ label, value, closable, onClose }) => (
                  <Tag closable={closable} onClose={onClose} key={value} style={{ marginRight: 2 }}>
                    {label}
                  </Tag>
                )}
                showSearch
                optionFilterProp="label"
                allowClear
              />
            </div>
            <div style={{ flex: "0 1 280px", minWidth: 160 }}>
              <span className="generalStyles__inputLabel">Assign by Role</span>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                placeholder="Select a role to assign all its users"
                value={undefined}
                onChange={(roleId: string) => handleAssignByRole(record.id, roleId)}
                options={roleOptions}
                allowClear
              />
            </div>
          </div>
        </WizardOpGate>
      </div>
    );
  };

  const isLoading = isLoadingPost || isLoadingPut || isLoadingDelete;

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoading}>
      <div style={{ padding: "16px" }}>
        <DrawerRowDivider icon={faEnvelopeCircleCheck} title="Notifications" />
        <div className={`drawer__box__${theme}`}>
          {/* BANK SELECTOR */}
          <div className="container">
            <div className="row">
              <div className="col-12 mt-3">
                <span className="generalStyles__inputLabel">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  Bank
                </span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  placeholder="Select a bank"
                  value={selectedBankId}
                  onChange={(value: string) => {
                    setSelectedBankId(value);
                    setNotifMessageId("");
                    setNotifMessageName("");
                    if (editingNotifId) {
                      setEditingNotifId(null);
                      clearForm();
                    }
                  }}
                  size="large"
                >
                  {wizardState.bankIds.map((bankId) => (
                    <Select.Option key={bankId} value={bankId}>
                      {wizardState.bankNames[bankId] || bankId}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </div>
        <br />

        {/* NOTIFICATIONS TABLE */}
        <div className={`drawer__box__${theme}`}>
          <Table
            dataSource={filteredNotifications}
            columns={notificationColumns}
            pagination={false}
            size="small"
            rowKey="id"
            locale={{ emptyText: "No notifications for this bank" }}
            expandable={{ expandedRowRender }}
          />
        </div>
        <br />

        {/* NOTIFICATION FORM */}
        <div className={`drawer__box__${theme}`}>
          <WizardReadOnlyId label="Notification ID" value={editingNotifId} />
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <FormInputText
                  inputValue={notifName}
                  inputLabelName={t("general.name")}
                  inputOnChange={(_newValue: string) => onChangeFormValue(_newValue)}
                  inputVariableName="notifName"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 mt-3">
                <span className="generalStyles__inputLabel">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  Recipients
                </span>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                  Choose a channel for each recipient; the destination field adapts — an email
                  address for Email, a phone number for SMS, a WhatsApp number for WhatsApp.
                </div>
                {notifRecipients.map((recipient, index) => (
                  <div
                    key={index}
                    style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}
                  >
                    <Select
                      data-testid={`recipient-type-${index}`}
                      style={{ flex: "0 0 150px" }}
                      value={recipient.type}
                      onChange={(value: string) => handleRecipientChange(index, "type", value)}
                      options={NOTIFICATION_TYPE_OPTIONS}
                      size="large"
                    />
                    <Input
                      style={{ flex: "1 1 auto" }}
                      size="large"
                      type={recipient.type === "EMAIL" ? "email" : "tel"}
                      placeholder={recipientPlaceholder(recipient.type)}
                      value={recipient.value}
                      status={
                        recipient.value.trim() !== "" && !isRecipientValid(recipient) ? "error" : ""
                      }
                      onChange={(e) => handleRecipientChange(index, "value", e.target.value)}
                    />
                    {/* Channels are a multi-select, not the single Type above:
                        one destination can be worth both emailing and texting,
                        and duplicating the whole rule per channel is what this
                        replaces. */}
                    <Select
                      data-testid={`recipient-channels-${index}`}
                      mode="multiple"
                      allowClear
                      style={{ flex: "0 0 220px" }}
                      size="large"
                      placeholder="Channels"
                      value={recipient.channels}
                      onChange={(values: string[]) => handleRecipientChannels(index, values)}
                      options={NOTIFICATION_TYPE_OPTIONS}
                    />
                    <Button
                      danger
                      size="large"
                      onClick={() => handleRemoveRecipientRow(index)}
                      disabled={notifRecipients.length <= 1}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="dashed" size="large" onClick={handleAddRecipientRow}>
                  + Add recipient
                </Button>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mt-3">
                <span className="generalStyles__inputLabel">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  LN2 Message
                </span>
                <div style={{ marginTop: 4 }}>
                  <SelectLn2Message
                    value={notifMessageId}
                    onChange={(key: string) => {
                      setNotifMessageId(key);
                      // "Category::Option" — matches decodeFlattenedMessage's format so a
                      // freshly-picked message and one rehydrated from the server (after a
                      // PUT or a reload) render identically, and so triggerSubject()'s
                      // "::" split works before the page has ever round-tripped (LIFT-940).
                      const displayName = key ? getFullDisplayName(key) : "";
                      setNotifMessageName(displayName);
                      if (!notifName.trim() && displayName) {
                        setNotifName(displayName);
                      }
                    }}
                    messageFilter={effectiveMessageFilter}
                    // Notifications never send the variant (LIFT-940): buildBodyForMessage
                    // only reads parsed.categoryKey/enumValue, and every persisted
                    // message_id decodes back to "normal" regardless of what was picked
                    // here. Lock the toggle so the form doesn't offer a choice with no
                    // effect.
                    disableVariantToggle
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <Switch
                    data-doc-label="Show all LN2 messages"
                    data-testid="show-all-ln2-messages"
                    checked={ignoreMessageFilter}
                    onChange={(checked) => setIgnoreMessageFilter(checked)}
                    disabled={!selectedBankId}
                  />
                  <span>Show all LN2 messages</span>
                </div>
                {ignoreMessageFilter && adapterMessageFilter !== undefined && (
                  <span className="generalStyles__infoText">
                    Showing every LN2 message, not just those this adapter reported as keys. Confirm
                    the controller actually produces a message before notifying on it.
                  </span>
                )}
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Fires on</span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  size="large"
                  value={matchMode}
                  onChange={setMatchMode}
                  options={MATCH_MODE_OPTIONS}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">When the message is</span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  size="large"
                  value={triggerEdge}
                  onChange={setTriggerEdge}
                  options={TRIGGER_EDGE_OPTIONS}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <span className="generalStyles__infoText">
                  {describeTrigger(matchMode, triggerEdge, notifMessageName)}
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <span className="generalStyles__inputLabel">Cooldown (s)</span>
                <InputNumber
                  style={{ width: "100%", marginTop: 4 }}
                  min={0}
                  value={notifCooldown}
                  onChange={(v) => setNotifCooldown(v || 300)}
                  size="large"
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <span className="generalStyles__inputLabel">Min Span (s)</span>
                <InputNumber
                  style={{ width: "100%", marginTop: 4 }}
                  min={0}
                  value={notifMinSpan}
                  onChange={(v) => setNotifMinSpan(v || 15)}
                  size="large"
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 24,
                  }}
                >
                  <Switch checked={notifMuted} onChange={(checked) => setNotifMuted(checked)} />
                  <span>Muted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <br />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                {editingNotifId ? (
                  <>
                    <div className="generalStyles__mrFix">
                      <WizardOpGate op="update">
                        <Button
                          type="primary"
                          onClick={handleUpdateNotification}
                          disabled={!isFormValid()}
                          loading={isLoadingPut}
                          size="large"
                        >
                          Update Notification
                        </Button>
                      </WizardOpGate>
                    </div>
                    <div className="generalStyles__mrFix">
                      <Button onClick={cancelEdit} size="large">
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="generalStyles__mrFix">
                      <WizardOpGate op="create">
                        <Button
                          type="dashed"
                          onClick={handleAddNotification}
                          disabled={!isFormValid()}
                          loading={isLoadingPost}
                          size="large"
                        >
                          + Add Notification
                        </Button>
                      </WizardOpGate>
                    </div>
                    <div className="generalStyles__mrFix">
                      <WizardOpGate op="create">
                        <Button
                          onClick={handleAddAllMessages}
                          disabled={!selectedBankId || availableMessages.length === 0}
                          loading={isLoadingPost}
                          size="large"
                        >
                          Add All Messages
                        </Button>
                      </WizardOpGate>
                    </div>
                    <div className="generalStyles__mrFix">
                      <Button onClick={handleSkip} size="large">
                        Skip Notifications
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* EXPORT BUTTON */}
        <Divider />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                <div className="generalStyles__mrFix">
                  {/* Backend stage 9 = Notifications. Previously hardcoded as
                      8 (pre-ChannelModifiers numbering), which downloaded the
                      Users & Roles scope instead. */}
                  <WizardExportButton stage={9} orgId={wizardState.organizationId} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ALERTS */}
        <GlobalAlert2
          isError={isErrorPost || isErrorPut}
          isSuccess={isSuccessPost || isSuccessPut}
          requestType={editingNotifId ? "PUT" : "POST"}
          error={errorPost || errorPut}
          name="Notification"
        />
      </div>
    </Spin>
  );
}
